import type { ReactNode } from 'react';
import { useOutreachKpis } from '@discovr/features';
import type { OutreachKpis } from '@discovr/types';
import { EmptyState } from '../../../components/common/EmptyState';
import { UsersThree } from '../../../design-system/icons';
import { ACCEPT_RATE_TITLE, CrmAcceptRateCard } from './CrmAcceptRateCard';
import { CrmCard, CrmCardError, CrmCardSkeleton } from './CrmCard';
import { CrmFunnelCard, FUNNEL_TITLE } from './CrmFunnelCard';
import { CrmKpiTiles, CrmKpiTilesSkeleton } from './CrmKpiTiles';
import { CrmMailboxHealthCard } from './CrmMailboxHealthCard';
import { CrmStageMixCard, STAGE_MIX_TITLE } from './CrmStageMixCard';
import { crmLoadErrorCopy } from './crmLoadError';
import { creatorsTotal } from './crmOverviewModel';

/** What the KPI cards call their content in an error line. */
export const KPIS_SUBJECT = 'these numbers';
export const OVERVIEW_EMPTY_TITLE = 'No creators in outreach yet';
export const OVERVIEW_EMPTY_BODY = 'Add creators from the Inbox and their progress shows here.';

/**
 * The Overview: four tiles, then two rows of two cards. The pipeline row
 * (Funnel, By stage) sits above the sending row (Mailbox health, agent
 * accept-rate). The creator list lives only on the Creators tab, so Overview
 * keeps to numbers. Mailbox health reads its own route, so it keeps working
 * when the KPI read fails.
 */
export function CrmOverviewTab({ campaignId }: { campaignId: string }) {
  const { data: kpis, isLoading, error } = useOutreachKpis(campaignId);

  if (kpis && creatorsTotal(kpis) === 0) {
    return <EmptyState icon={UsersThree} title={OVERVIEW_EMPTY_TITLE} body={OVERVIEW_EMPTY_BODY} />;
  }
  return (
    <div className="space-y-4">
      <TilesRow kpis={kpis} isLoading={isLoading} />
      <div className="grid gap-4 lg:grid-cols-2">
        <KpiCardSlot title={FUNNEL_TITLE} kpis={kpis} isLoading={isLoading} error={error}>
          {(loaded) => <CrmFunnelCard steps={loaded.funnelSteps} />}
        </KpiCardSlot>
        <KpiCardSlot title={STAGE_MIX_TITLE} kpis={kpis} isLoading={isLoading} error={error}>
          {(loaded) => <CrmStageMixCard mix={loaded.stageMix} />}
        </KpiCardSlot>
        <CrmMailboxHealthCard campaignId={campaignId} />
        <KpiCardSlot title={ACCEPT_RATE_TITLE} kpis={kpis} isLoading={isLoading} error={error}>
          {(loaded) => <CrmAcceptRateCard kpis={loaded} />}
        </KpiCardSlot>
      </div>
    </div>
  );
}

function TilesRow({ kpis, isLoading }: { kpis: OutreachKpis | undefined; isLoading: boolean }) {
  if (kpis) return <CrmKpiTiles kpis={kpis} />;
  if (isLoading) return <CrmKpiTilesSkeleton />;
  return null;
}

interface KpiCardSlotProps {
  title: string;
  kpis: OutreachKpis | undefined;
  isLoading: boolean;
  error: unknown;
  children: (kpis: OutreachKpis) => ReactNode;
}

/** Renders the card once the KPIs are in; until then a titled card with a skeleton, or its error line. */
function KpiCardSlot({ title, kpis, isLoading, error, children }: KpiCardSlotProps) {
  if (kpis) return <>{children(kpis)}</>;
  return (
    <CrmCard title={title}>
      <KpiCardPlaceholder isLoading={isLoading} error={error} />
    </CrmCard>
  );
}

function KpiCardPlaceholder({ isLoading, error }: { isLoading: boolean; error: unknown }) {
  if (error) return <CrmCardError>{crmLoadErrorCopy(KPIS_SUBJECT, error)}</CrmCardError>;
  if (isLoading) return <CrmCardSkeleton />;
  return null;
}
